import {
  FeatureFlagType,
  getFeatureFlagMeta,
  isFeatureFlagKey,
  KNOWN_FEATURE_FLAG_KEYS,
} from "encamp-shared/src/featureFlags";
import { FilterKey } from "hooks/useOmnisearchDatagrid";

/**
 * Render a feature-flag key as its human label (falling back to the
 * raw key for any value the type guard rejects — e.g. an unknown key
 * persisted from a stale URL filter that we no longer ship).
 */
const flagKeyToLabel = (key: string): string =>
  isFeatureFlagKey(key) ? getFeatureFlagMeta(key).label : key;

const MODULE_FLAG_KEYS = [...KNOWN_FEATURE_FLAG_KEYS]
  .filter((k) => getFeatureFlagMeta(k).type === FeatureFlagType.Module)
  .sort((a, b) => flagKeyToLabel(a).localeCompare(flagKeyToLabel(b)));

const ALL_FLAG_KEYS_SORTED = [...KNOWN_FEATURE_FLAG_KEYS].sort((a, b) =>
  flagKeyToLabel(a).localeCompare(flagKeyToLabel(b))
);

/**
 * Filter columns for tenant-scoped grids that want to filter by which
 * feature flags a tenant has enabled. Both filters serialize to the
 * same server-side `hasFlag` omnisearch key via keyMap aliases in the
 * `tenants` resolver — the only difference is which keys are
 * selectable in the chip picker:
 *
 *   - `Modules Enabled` — top-level Module flags only.
 *   - `Flags Enabled`  — every known flag (modules, add-ons, "other").
 *
 * Both filters AND across selected keys (a tenant must have *all* of
 * the selected flags enabled to match), per the `hasFlag` resolver
 * contract.
 *
 * Used by:
 *   - Staff → Feature Flags page (tenant-centric grid)
 *   - Staff → Organizations page (`OrganizationTable`)
 *
 * Keep this in sync with `apps/api/src/resolvers/tenants.ts`
 * `selectToOmnisearchKeyMap` aliases.
 */
export const TENANT_FEATURE_FLAG_FILTER_COLUMNS: FilterKey[] = [
  {
    key: "modulesEnabled",
    header: "Modules Enabled",
    filterKeyType: "enumAutocomplete",
    enumValues: MODULE_FLAG_KEYS,
    enumPresentationFunction: flagKeyToLabel,
  },
  {
    key: "flagsEnabled",
    header: "Flags Enabled",
    filterKeyType: "enumAutocomplete",
    enumValues: ALL_FLAG_KEYS_SORTED,
    enumPresentationFunction: flagKeyToLabel,
  },
];
